import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from '@mui/material';
import { useEffect, useState, type ReactNode } from 'react';
import type { UploadModule } from '@/api/files';
import type { StorageView } from '@/api/storage-admin';
import { DialogClose } from '@/components/DialogClose';
import {
  formatBytes, FormFields, useFieldForm, valueAt, withValueAt, type FieldDef, type FieldForm, type FieldValues,
} from '@/components/fields';
import { ApiError } from '@/lib/api-client';

export interface FieldGroup {
  title: string;
  hint?: string;
  fields: FieldDef[];
}

export const MODULE_LABELS: Record<UploadModule, string> = {
  it_proof: 'IT declaration proofs',
  reimbursement: 'Reimbursement bills',
  flexi_proof: 'Flexi benefit bills',
  loan_document: 'Loan request documents',
  form: 'Attachments on masters (Form.io)',
};

/** A whole-number field; the range mirrors the server's, which still decides. */
export function whole(name: string, label: string, min: number, max?: number, extra: Partial<FieldDef> = {}): FieldDef {
  return { type: 'number', name, label, validate: { min, max }, config: { step: 1 }, ...extra };
}

/** Only the fields whose value moved, nested the way the server's settings are. */
export function changedValues(fields: FieldDef[], before: FieldValues, after: FieldValues): FieldValues {
  return fields.reduce<FieldValues>((patch, f) => {
    const value = valueAt(after, f.name);
    return JSON.stringify(value) === JSON.stringify(valueAt(before, f.name)) ? patch : withValueAt(patch, f.name, value);
  }, {});
}

/**
 * The field a refused save names, so the message can sit under it: a settings
 * refusal carries `detail.field`, a request-validation one `detail[].loc`.
 */
export function serverError(e: unknown): { field?: string; message: string } {
  if (!(e instanceof ApiError)) return { message: e instanceof Error ? e.message : 'The change could not be saved.' };
  const detail = (e.body as { detail?: unknown } | null)?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const { field, message } = detail as { field?: string; message?: string };
    if (field) return { field, message: message?.replace(`${field}: `, '') ?? e.detailMessage };
  }
  if (Array.isArray(detail) && detail[0]) {
    const { loc, msg } = detail[0] as { loc?: unknown[]; msg?: string };
    const field = loc?.filter((p) => p !== 'body').join('.');
    if (field) return { field, message: msg ?? e.detailMessage };
  }
  return { message: e.detailMessage };
}

const HEALTH_COLOR: Record<string, 'success' | 'error' | 'default'> = { OK: 'success', FAIL: 'error' };

export function HealthChip({ status }: { status: string | null }) {
  return <Chip size="small" variant="outlined" color={HEALTH_COLOR[status ?? ''] ?? 'default'} label={status ?? 'Not checked'} />;
}

/** Where a workspace's files are, how much it holds and what is waiting — the same strip everywhere. */
export function StorageSummary({ view }: { view: StorageView }) {
  const active = view.profiles.find((p) => p.id === view.platform.active_profile_id);
  const ceiling = view.platform.quota_ceiling_bytes;
  const items: [string, string][] = [
    ['Stored on', view.mode === 'S3' ? `S3 · ${active ? `${active.name} (${active.bucket})` : 'no server chosen'}` : 'This server'],
    ['Used', `${formatBytes(view.usage.used_bytes)}${ceiling ? ` of ${formatBytes(ceiling)}` : ''}`],
    ['Files', view.usage.file_count.toLocaleString('en-IN')],
    ['Waiting to reach S3', String(view.queue.pending_sync)],
  ];
  return (
    <Box>
      <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {items.map(([label, value]) => (
          <Box key={label}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
          </Box>
        ))}
        {active && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Server health</Typography>
            <HealthChip status={active.health_status} />
          </Box>
        )}
      </Stack>
      {view.queue.failed > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {view.queue.failed} file{view.queue.failed === 1 ? '' : 's'} could not be moved to S3 after every retry.
          They are still safe on this server; see Logs to retry them.
        </Alert>
      )}
      {view.mode === 'S3' && !active && (
        <Alert severity="error" sx={{ mt: 1.5 }}>S3 is switched on but no server is chosen, so files stay on this server.</Alert>
      )}
    </Box>
  );
}

interface FieldsDialogProps {
  open: boolean;
  title: string;
  fields: FieldDef[];
  /** Keep the same object between renders: a new one resets the form. */
  initial: FieldValues;
  submitLabel?: string;
  onSubmit: (values: FieldValues) => Promise<unknown>;
  onClose: () => void;
  /** Runs instead of the plain update, for a field that sets others (a provider filling in its defaults). */
  onChange?: (name: string, value: unknown, form: FieldForm) => void;
  /** The form field showing a value the server knows by another name. */
  fieldFor?: (serverField: string, values: FieldValues) => string;
  children?: ReactNode;
}

/** A small form in a dialog, built from field definitions; a refused save lands under the field it names. */
export function FieldsDialog({
  open, title, fields, initial, submitLabel = 'Save', onSubmit, onClose, onChange, fieldFor, children,
}: FieldsDialogProps) {
  const form = useFieldForm(fields, initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const { reset } = form;

  useEffect(() => {
    if (open) {
      reset(initial);
      setError(undefined);
    }
  }, [open, initial, reset]);

  const submit = async () => {
    setError(undefined);
    if (!form.validate()) return;
    setBusy(true);
    try {
      await onSubmit(form.values);
      onClose();
    } catch (e) {
      const { field, message } = serverError(e);
      const name = field && (fieldFor ? fieldFor(field, form.values) : field);
      if (name && fields.some((f) => f.name === name)) form.setErrors({ [name]: message });
      else setError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title} <DialogClose onClose={onClose} /></DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {children}
          <FormFields fields={fields} values={form.values} errors={form.errors}
                      onChange={(name, value) => (onChange ? onChange(name, value, form) : form.setValue(name, value))} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy}>{busy ? 'Saving…' : submitLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}
